/**
 * ConversionResult Component
 *
 * Displays conversion result with download button.
 * In desktop app mode, shows "Show in Folder" as primary action.
 */

import { memo, useCallback, useState } from 'react';
import {
  CheckCircle,
  Download,
  RefreshCw,
  FileAudio,
  FileVideo,
  FolderOpen,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getConversionDownloadUrl,
  type ConversionResult as ConversionResultType,
} from '../../hooks/useMediaConversion';
import { isDesktopApp } from '../../utils/apiMode';
import { openVideoFolder as electronOpenVideoFolder } from '../../utils/electronBridge';
import { formatFileSize } from '@/constants/fileSize';
import { downloadFile } from '@/utils/downloadProxy';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/Toast';

interface ConversionResultProps {
  result: ConversionResultType;
  mediaType: 'audio' | 'video';
  onConvertAnother: () => void;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const ConversionResult = memo(function ConversionResult({
  result,
  mediaType,
  onConvertAnother,
}: ConversionResultProps) {
  const { t } = useTranslation();
  const { toasts, showToast, removeToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  // Check if running in Electron desktop app (uses centralized apiMode utility)
  const isDesktop = isDesktopApp();
  // Reveal-in-Finder requires BOTH the desktop shell and a real local file:
  // a cloud conversion has only an https download URL.
  const showOpenFolder = isDesktop && Boolean(result.outputPath);

  const handleDownload = useCallback(async () => {
    if (!result.downloadUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      // The link that came with the result may have expired; ask for a fresh one.
      let url: string;
      try {
        url = await getConversionDownloadUrl(result.jobId);
      } catch (error) {
        console.error('Could not renew the download link:', error);
        showToast('error', t('result.linkRenewal.title'), t('result.linkRenewal.message'));
        return;
      }
      await downloadFile(url, result.outputFileName);
    } catch {
      showToast(
        'error',
        t('converter.result.downloadFailedTitle', { defaultValue: 'Download failed' }),
        t('converter.result.downloadFailedMessage', {
          defaultValue: 'Could not download the converted file. Please try again.',
        })
      );
    } finally {
      setIsDownloading(false);
    }
  }, [result.downloadUrl, result.jobId, result.outputFileName, isDownloading, showToast, t]);

  /**
   * Open the folder containing the converted file (desktop app only)
   */
  const handleOpenFolder = useCallback(async () => {
    // "Show in Folder" needs the local filesystem path of the converted file.
    // Falling back to `|| result.downloadUrl` handed an "http://.../convert/
    // download/xyz" string to shell.showItemInFolder(), which is a silent
    // no-op: the button appeared to work and did nothing at all.
    if (!showOpenFolder || !result.outputPath) return;

    const opened = await electronOpenVideoFolder(result.outputPath);
    if (!opened) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.errorMessage'));
    }
  }, [showOpenFolder, result.outputPath, showToast, t]);

  const Icon = mediaType === 'video' ? FileVideo : FileAudio;
  const iconColor = mediaType === 'video' ? 'text-blue-500' : 'text-purple-500';

  return (
    <div className="card-themed-lg">
      {/* Success header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white">
          {t('converter.result.success', { defaultValue: 'Conversion complete!' })}
        </h3>
      </div>

      {/* File info */}
      <div className="p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg mb-6">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-white dark:bg-secondary-800 rounded-lg">
            <Icon className={`w-8 h-8 ${iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-secondary-900 dark:text-white truncate">
              {result.outputFileName}
            </p>
            <div className="flex flex-wrap gap-2 mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              {result.outputSize > 0 && <span>{formatFileSize(result.outputSize)}</span>}
              {result.outputSize > 0 && result.duration && <span>|</span>}
              {result.duration && <span>{formatDuration(result.duration)}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Primary action: reveal the local file when there IS one, else download.
            Gating on `isDesktop` alone left desktop users with a cloud-converted
            file staring at a "Show in Folder" button that could never work, and
            no way to download. */}
        {showOpenFolder ? (
          <button
            onClick={handleOpenFolder}
            disabled={!result.outputPath}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-secondary-400 text-white font-medium rounded-lg transition-colors"
          >
            <FolderOpen className="w-5 h-5" />
            {t('result.buttons.showInFinder', { defaultValue: 'Show in Folder' })}
          </button>
        ) : (
          <button
            onClick={() => void handleDownload()}
            disabled={!result.downloadUrl || isDownloading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-secondary-400 text-white font-medium rounded-lg transition-colors"
          >
            {isDownloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            {isDownloading
              ? t('common.downloading', { defaultValue: 'Downloading...' })
              : t('converter.result.download', { defaultValue: 'Download' })}
          </button>
        )}

        {/* Desktop with a local file: offer Download as a secondary action too.
            Keyed off showOpenFolder, not isDesktop — otherwise a desktop user
            whose primary action already IS Download gets the button twice. */}
        {showOpenFolder && (
          <button
            onClick={() => void handleDownload()}
            disabled={!result.downloadUrl || isDownloading}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 disabled:opacity-50 font-medium rounded-lg transition-colors"
          >
            {isDownloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            {isDownloading
              ? t('common.downloading', { defaultValue: 'Downloading...' })
              : t('converter.result.download', { defaultValue: 'Download' })}
          </button>
        )}

        <button
          onClick={onConvertAnother}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 font-medium rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
          {t('converter.result.convertAnother', { defaultValue: 'Convert Another' })}
        </button>
      </div>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
});

export default ConversionResult;
